import { animate, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import type { ECharts, EChartsOption } from 'echarts';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/core/services/auth.service';
import { ImportCompanyProductsComponent, ImportCompanyProductsDialogData } from 'src/app/shared/components/import-company-products/import-company-products.component';
import { ImportProductCompaniesComponent, ImportProductCompaniesDialogData } from 'src/app/shared/components/import-product-companies/import-product-companies.component';
import { AiChatService, ChatCommand, ChatMessage } from '../services/ai-chat.service';

import { TechstacksImportComponent } from 'src/app/shared/components/techstacks-import/techstacks-import.component';
import { GeminiService } from '../services/gemini-service.service';
import { GeminiSchemaType, ProxyAiResponseSchemaService } from '../services/proxy-ai-response-schema.service';
import { SUGGESTIONS } from './sugesstions.comstant';

interface RachelModeChip {
  command: ChatCommand;
  label: string;
  icon: string;
  requiresPeopleViewer?: boolean;
}

@Component({
  selector: 'ai-chat',
  templateUrl: './ai-chat.component.html',
  styleUrls: ['./ai-chat.component.scss'],
  animations: [
    trigger('messageAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate(
          '0.5s ease-out',
          style({ opacity: 1, transform: 'translateY(0)' })
        ),
      ]),
    ]),
  ],
})
export class AiChatComponent implements OnInit, OnDestroy, AfterViewInit {
  userInput = '';
  isFullScreen = false;
  chatCommand = ChatCommand;
  suggestions: string[];
  readonly homePromptStarters: string[] = [
    'Analyze the use of ServiceNow by companies for IT service management.',
    'What are the key trends affecting ERP vendors like SAP, Oracle, and Workday?',
    'List the software products offered by Zoom, focusing on data from their website and social media.',
    'Which tools are recommended for project management in remote teams?',
  ];
  isAIAdmin = false;
  isPeopleViewer = false;
  readonly modeChips: RachelModeChip[] = [
    {
      command: ChatCommand.FindCustomers,
      label: 'Discover Customers',
      icon: 'groups',
    },
    {
      command: ChatCommand.AnalyzeTechStacks,
      label: 'Discover Tech Stacks',
      icon: 'hub',
    },
    {
      command: ChatCommand.TrendAnalysis,
      label: 'Analyze Product Trends',
      icon: 'trending_up',
    },
    {
      command: ChatCommand.ProductAnalysis,
      label: 'Analyze Products',
      icon: 'inventory_2',
    },
    {
      command: ChatCommand.RecommendSoftware,
      label: 'Recommend Products',
      icon: 'recommend',
    },
    {
      command: ChatCommand.BenchmarkCompanies,
      label: 'Benchmark Companies',
      icon: 'query_stats',
    },
    {
      command: ChatCommand.FindEmployees,
      label: 'Find People',
      icon: 'person_search',
      requiresPeopleViewer: true,
    },
  ];
  rachelPdfExportingMessage: ChatMessage | undefined;
  generatedUiSelection:
    | { blockTitle: string; title: string; detail?: string; value?: string | number }
    | undefined;
  private readonly generatedEchartOptionsCache = new WeakMap<object, EChartsOption>();
  private readonly generatedCharts = new Set<ECharts>();
  private generatedChartResizeTimers: Array<ReturnType<typeof setTimeout>> = [];
  private scrollAnimationFrame: number | undefined;
  private shouldStickToBottom = true;
  private readonly scrollBottomThreshold = 88;
  showJumpToLatest = false;

  @ViewChild('chatWindow') chatWindow!: ElementRef;
  @ViewChild('inputField') inputField!: ElementRef;

  private originalWidth: string | undefined;
  private originalHeight: string | undefined;
  private originalPosition: { left?: string; right?: string; top?: string; bottom?: string } | undefined;

  private destroy$ = new Subject<void>();

  shortCompanyName = '';

  private readonly exportModeConfigs: Record<
    'product' | 'company',
    {
      headers: string[];
      aliases: Record<string, string[]>;
      mergeKeyFields: string[][];
    }
  > = {
    product: {
      headers: [
        'company',
        'company_domain',
        'city',
        'state',
        'industry',
        'employee_range',
        'revenue_range',
        'vendor',
        'product',
        'date_first_verified',
        'date_last_verified',
      ],
      aliases: {
        company: ['company', 'company_name'],
        company_domain: [
          'company_domain',
          'companydomain',
          'company_domain_name',
          'companydomainname',
        ],
        city: ['city'],
        state: ['state'],
        industry: ['industry'],
        employee_range: [
          'employee_range',
          'employee_range_text',
          'employment_range',
          'employees',
        ],
        revenue_range: [
          'revenue_range',
          'revenue',
          'revenue_range_text',
          'revenue_band',
        ],
        vendor: ['vendor', 'product_vendor', 'product_vendor_domain_name'],
        product: ['product', 'product_name'],
        date_first_verified: ['date_first_verified', 'datefirstverified'],
        date_last_verified: ['date_last_verified', 'datelastverified'],
      },
      mergeKeyFields: [
        ['company_domain', 'product'],
        ['company', 'product'],
        ['company_domain', 'vendor'],
        ['company', 'vendor'],
      ],
    },
    company: {
      headers: [
        'company',
        'company_domain',
        'product',
        'vendor',
        'main_category',
        'parent_category',
        'date_first_verified',
        'date_last_verified',
      ],
      aliases: {
        company: ['company', 'company_name'],
        company_domain: [
          'company_domain',
          'companydomain',
          'company_domain_name',
          'companydomainname',
        ],
        product: ['product', 'product_name'],
        vendor: ['vendor', 'vendor_name', 'product_vendor'],
        main_category: ['main_category', 'maincategory', 'primary_category'],
        parent_category: ['parent_category', 'parentcategory'],
        date_first_verified: ['date_first_verified', 'datefirstverified'],
        date_last_verified: ['date_last_verified', 'datelastverified'],
      },
      mergeKeyFields: [
        ['company_domain', 'product'],
        ['company', 'product'],
        ['company_domain', 'vendor'],
        ['company', 'vendor'],
      ],
    },
  };

  constructor(
    public aiChatService: AiChatService,
    public dialogRef: MatDialogRef<AiChatComponent>,
    private dialog: MatDialog,
    private router: Router,
    private authService: AuthService,
    private proxyAiResponseSchemaService: ProxyAiResponseSchemaService,
    private geminiService: GeminiService,
    private toastr: ToastrService,
  ) {}

  @HostListener('click', ['$event'])
  // onClick(event: MouseEvent) {

  //   // If we don't have an anchor tag, we don't need to do anything.
  //   console.log(event.target);
  //   console.log(event.target instanceof HTMLAnchorElement );
  //   if (event.target instanceof HTMLAnchorElement === false) {
  //     console.log('exit');
  //     return;
  //   }
  //   // Prevent page from reloading
  //   event.preventDefault();
  //   let target = <HTMLAnchorElement>event.target;
  //   console.log('nvigate');
  //   // Navigate to the path in the link
  //   this.router.navigate([target.pathname]);
  // }
  onClick(event: MouseEvent) {
    const anchor = (event.target as HTMLElement).closest('a');

    // Если ссылка не найдена — выходим
    if (!anchor) {
      return;
    }

    // Предотвращаем стандартное поведение перехода
    event.preventDefault();

    const target = anchor as HTMLAnchorElement;
    console.log('Navigate to:', target.pathname);
    if (!target.host.startsWith(window.location.host)) {
      window.open(anchor.href, '_blank');
    }
    // Навигация
    this.router.navigate([target.pathname]);
  }
  ngOnInit(): void {
    this.isPeopleViewer = this.authService.checkPeopleViewerRole();
    this.suggestions = SUGGESTIONS.General;
    this.isAIAdmin = this.authService.checkAIAdminRole();
    this.aiChatService.chatHistory$
      .pipe(takeUntil(this.destroy$))
      .subscribe((history) => {
        if (history.length === 0 && !this.aiChatService.hiFlag) {
          this.aiChatService.sendMessage('Hi! ');
        }
      });
  }

  ngAfterViewInit(): void {
    this.inputField.nativeElement.focus();
    this.aiChatService.chatHistory$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        setTimeout(() => this.scrollToBottom(), 0);
        this.scheduleGeneratedChartResize();
      });
  }

  async sendMessage(): Promise<void> {
    const trimmedMessage = this.userInput.trim();
    if (!trimmedMessage) return;

    this.userInput = '';
    this.shortCompanyName = '';
    this.shouldStickToBottom = true;
    setTimeout(() => this.scrollToBottom({ force: true, smooth: true }), 0);

    try {
      await this.aiChatService.sendMessage(trimmedMessage);
    } catch (error) {
      console.error('Error receiving response:', error);
    } finally {
      setTimeout(() => this.scrollToBottom({ smooth: true }), 0);
    }
  }

  onKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  onAggressiveModeToggle(event: MatSlideToggleChange): void {
    this.aiChatService.setAggressiveCustomersMode(event.checked);
  }

  canShowModeChip(chip: RachelModeChip): boolean {
    return !chip.requiresPeopleViewer || this.isPeopleViewer;
  }


  shouldShowPromptStarters(history: any[] | null | undefined): boolean {
    const realMessages = (history || []).filter(message => {
      const text = String(message?.text || '').replace(/<[^>]*>/g, '').trim();
      return message?.sender === 'user' || (message?.sender === 'bot' && text && !/^hi[!,\.\s]/i.test(text));
    });

    return realMessages.length <= 1 && !this.aiChatService.isLoading;
  }

  getPromptStarters(): string[] {
    return this.homePromptStarters;
  }

  usePromptStarter(prompt: string): void {
    this.userInput = prompt;
    this.inputField?.nativeElement?.focus();
  }

  async handleGeminiSourceClick(source: any): Promise<void> {
    if (!this.isAIAdmin) {
      return;
    }

    console.log(source);

    const { AddTechInstallDialogComponent } = await import(
      'src/app/shared/components/add-tech-install-dialog/add-tech-install-dialog.component'
    );

    this.dialog
      .open(AddTechInstallDialogComponent, {
        width: '720px',
        maxHeight: '90vh',
        data: {
          rawSource: source,
          mode: this.aiChatService.selectedMode,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result?.completed) {
          this.aiChatService.notifyGeminiSourceSelection(source);
        }
      });
  }

  private isChatCommandPrompt(value: string): boolean {
    return Object.values(ChatCommand).includes(value as ChatCommand);
  }

  chipSelected(value: string): void {
    this.shortCompanyName = '';
    switch (value) {
      case ChatCommand.FindCustomers:
        this.suggestions = SUGGESTIONS.FindCustomersOfProducts;
        break;
      case ChatCommand.AnalyzeTechStacks:
        this.suggestions = SUGGESTIONS.AnalyzeTechStacksForCompanies;
        break;
      case ChatCommand.BenchmarkCompanies:
        this.suggestions = SUGGESTIONS.BenchmarkCompanies;
        break;
      case ChatCommand.GeneralMode:
        this.suggestions = SUGGESTIONS.General;
        break;
      case ChatCommand.RecommendSoftware:
        this.suggestions = SUGGESTIONS.RecommendedSoftware;
        break;
      case ChatCommand.ProductAnalysis:
        this.suggestions = SUGGESTIONS.AnalyzeProduct;
        break;
      case ChatCommand.TrendAnalysis:
        this.suggestions = SUGGESTIONS.TrendAnalysis;
        break;
      case ChatCommand.FindEmployees:
        this.suggestions = SUGGESTIONS.FindEmployeesOfCompanies;
        break;
      case ChatCommand.ApiDocs:
        this.suggestions = SUGGESTIONS.ApiDocs;
        break;
      default:
        this.suggestions = SUGGESTIONS.General;
        break;
    }
    this.userInput = value;
    this.sendMessage();
    this.inputField.nativeElement.focus();
  }

  toggleExpand(): void {
    this.dialogRef.close();
  }

  onChatWindowScroll(): void {
    const element = this.chatWindow?.nativeElement;
    if (!element) {
      return;
    }

    const nearBottom = this.isNearBottom(element);
    if (nearBottom) {
      this.shouldStickToBottom = true;
      this.showJumpToLatest = false;
    } else if (!this.shouldStickToBottom) {
      this.showJumpToLatest = true;
    }
  }

  onChatWindowUserScroll(): void {
    const element = this.chatWindow?.nativeElement;
    if (!element) {
      return;
    }

    this.shouldStickToBottom = this.isNearBottom(element);
    this.showJumpToLatest = !this.shouldStickToBottom;

    if (!this.shouldStickToBottom) {
      this.cancelScrollAnimation();
    }
  }

  jumpToLatest(): void {
    this.shouldStickToBottom = true;
    this.showJumpToLatest = false;
    this.scrollToBottom({ force: true, smooth: true });
  }

  private scrollToBottom(options: { force?: boolean; smooth?: boolean } = {}): void {
    const element = this.chatWindow?.nativeElement;
    if (!element) {
      return;
    }

    const end = Math.max(0, element.scrollHeight - element.clientHeight);
    if (!options.force && !this.shouldStickToBottom && !this.isNearBottom(element)) {
      return;
    }

    this.shouldStickToBottom = true;
    this.showJumpToLatest = false;
    this.cancelScrollAnimation();

    if (!options.smooth) {
      element.scrollTop = end;
      return;
    }

    const start = element.scrollTop;
    const distance = end - start;
    if (Math.abs(distance) < 2) {
      element.scrollTop = end;
      return;
    }

    const duration = 120;
    let startTime: number | null = null;

    const animateScroll = (currentTime: number) => {
      if (!this.shouldStickToBottom) {
        this.cancelScrollAnimation();
        return;
      }

      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);

      element.scrollTop = start + distance * ease;

      if (progress < 1) {
        this.scrollAnimationFrame = requestAnimationFrame(animateScroll);
      } else {
        this.scrollAnimationFrame = undefined;
      }
    };

    this.scrollAnimationFrame = requestAnimationFrame(animateScroll);
  }

  private isNearBottom(element: HTMLElement): boolean {
    return element.scrollHeight - element.scrollTop - element.clientHeight <= this.scrollBottomThreshold;
  }

  private cancelScrollAnimation(): void {
    if (this.scrollAnimationFrame !== undefined) {
      cancelAnimationFrame(this.scrollAnimationFrame);
      this.scrollAnimationFrame = undefined;
    }
  }
  public clearChat(): void {
    this.aiChatService.clearChatHistory();
    this.shortCompanyName = '';
  }

  public closeChat(): void {
    this.clearChat();
    this.dialogRef.close();
  }

  public toggleSize(): void {
    if (this.isFullScreen) {
      this.restoreOriginalSize();
    } else {
      this.centerAndResize();
    }
    this.isFullScreen = !this.isFullScreen;
  }


  private centerAndResize(): void {
    if (!this.originalWidth || !this.originalHeight) {
      this.originalWidth = this.dialogRef._containerInstance._config.width;
      this.originalHeight = this.dialogRef._containerInstance._config.height;
      this.originalPosition =
        this.dialogRef._containerInstance._config.position || {};
    }

    const horizontalMargin = window.innerWidth >= 1200 ? 96 : 16;
    const availableWidth = window.innerWidth - horizontalMargin;
    const width = Math.min(Math.max(availableWidth - 70, 320), 1290);
    const height = Math.max(480, window.innerHeight - 70);
    const left = (window.innerWidth - width) / 2;

    this.dialogRef.updateSize(`${width}px`, `${height}px`);
    this.dialogRef.updatePosition({ left: `${left}px`, bottom: '0px' });
    this.dialogRef.removePanelClass('animate__slideInUp');
    this.dialogRef.addPanelClass(['animate__slideInRight', 'animate__zoomIn']);
  }

  private restoreOriginalSize(): void {
    if (this.originalWidth && this.originalHeight) {
      this.dialogRef.updateSize(this.originalWidth, this.originalHeight);
    }
    if (this.originalPosition) {
      this.dialogRef.updatePosition(this.originalPosition);
    }
    this.dialogRef.removePanelClass('animate__slideInRight');
    this.dialogRef.addPanelClass(['animate__slideInLeft', 'animate__zoomOut']);
  }

  ngOnDestroy(): void {
    this.cancelScrollAnimation();
    this.generatedChartResizeTimers.forEach(timer => clearTimeout(timer));
    this.generatedChartResizeTimers = [];
    this.generatedCharts.clear();
    this.destroy$.next();
    this.destroy$.complete();
  }

  openImportCompaniesDialog(rawSource: unknown) {
    const payload = JSON.stringify({ rawSource });

    const dialogData: ImportProductCompaniesDialogData = {
      data: payload,
      title: '',
    };

    this.dialog.open(ImportProductCompaniesComponent, {
      data: dialogData,
      width: '1100px',
      maxWidth: '1100px',
      disableClose: false,
    });
  }

  openImportProductsDialog(rawSource: unknown) {
    const payload = JSON.stringify({ rawSource });

    const dialogData: ImportCompanyProductsDialogData = {
      data: payload,
      title: '',
    };

    this.dialog.open(ImportCompanyProductsComponent, {
      data: dialogData,
      width: '1100px',
      maxWidth: '1100px',
      disableClose: false,
    });
  }

 openImportDialog(rawSource: any, type: 'companies' | 'products') {
  const title =
    type === 'companies'
      ? 'Import Companies into BuyerCaddy'
      : 'Import Products into BuyerCaddy';

  this.dialog.open(TechstacksImportComponent, {
    data: { techStacks: rawSource, title },
    disableClose: false,
  });
}

async preparingExportData(
  mode: 'product' | 'company',
  buyercaddyData: any,
  geminiData: any
): Promise<any> {
  const isProductMode = mode === 'product';

  const instruction = isProductMode
    ? 'Return an array of products; include or enrich data if necessary.'
    : 'Return an array of companies; include or enrich data if necessary.';

  const schemaType = isProductMode
    ? GeminiSchemaType.csvProducts
    : GeminiSchemaType.csvCompanies;

  const result = await this.proxyAiResponseSchemaService.analyzePrompt(
    instruction,
    JSON.stringify(geminiData),
    schemaType,
    0,
    'gemini-flash-latest'
  );

  this.exportData(mode, buyercaddyData, result);
}

  getColumns(data: any[] | undefined): string[] {
    if (!data || !Array.isArray(data) || data.length === 0) return [];
    const columns = new Set<string>();
    for (const item of data) {
      if (item && typeof item === 'object') {
        Object.keys(item).forEach(k => columns.add(k));
      }
    }
    return Array.from(columns);
  }

  onGeneratedChartInit(chart: ECharts): void {
    this.generatedCharts.add(chart);
    this.scheduleGeneratedChartResize();
  }

  private scheduleGeneratedChartResize(): void {
    this.generatedChartResizeTimers.forEach(timer => clearTimeout(timer));
    this.generatedChartResizeTimers = [40, 160, 420, 900].map(delay => setTimeout(() => {
      this.generatedCharts.forEach(chart => {
        if (!chart.isDisposed()) {
          chart.resize();
        }
      });
    }, delay));
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.scheduleGeneratedChartResize();
  }

  getGeneratedUiBlocks(message: any): any[] {
    const blocks = message?.generatedUi?.blocks;
    return Array.isArray(blocks)
      ? blocks.filter(block => block?.type !== 'recommendations')
      : [];
  }

  trackGeneratedUiBlock = (index: number, block: any): string => {
    return `${block?.type ?? 'block'}:${block?.id ?? block?.title ?? index}`;
  };

  getGeneratedBlockKicker(block: any): string {
    switch (block?.type) {
      case 'metric_cards':
        return 'Snapshot';
      case 'score_table':
        return 'Comparison';
      case 'bar_chart':
        return 'Ranking';
      case 'line_chart':
        return 'Momentum';
      case 'scatter_chart':
        return 'Signal map';
      case 'radar_chart':
        return 'Profile';
      case 'historical_trend_chart':
        return 'Historical evidence';
      case 'diagram':
        return 'Relationship map';
      case 'timeline':
        return 'Timeline';
      case 'recommendations':
        return 'Actions';
      default:
        return 'Insight';
    }
  }

  getBlockItems(block: any): any[] {
    const items = block?.items;
    return Array.isArray(items) ? items : [];
  }

  getBlockColumns(block: any): string[] {
    const columns = block?.columns;
    return Array.isArray(columns) ? columns : [];
  }

  getBlockRows(block: any): string[][] {
    const rows = block?.rows;
    return Array.isArray(rows) ? rows : [];
  }

  getChartSeries(block: any): any[] {
    const series = block?.series;
    return Array.isArray(series) ? series : [];
  }

  getFootprintRead(block: any):
    | { label: string; detail: string; topLabel: string; topValue: number; concentration: number }
    | undefined {
    if (block?.type !== 'bar_chart') {
      return undefined;
    }

    const text = `${block?.title || ''} ${block?.yLabel || ''} ${this.getChartSeries(block).map(item => item?.name || '').join(' ')}`;
    if (!/install|deployment|detection|detected|companies/i.test(text)) {
      return undefined;
    }

    const signals = this.getChartSignals(block)
      .map(signal => ({ label: String(signal?.label || ''), value: Number(signal?.total) || 0 }))
      .filter(signal => signal.label && signal.value > 0)
      .sort((left, right) => right.value - left.value);

    if (signals.length < 3) {
      return undefined;
    }

    const total = signals.reduce((sum, signal) => sum + signal.value, 0);
    const top = signals[0];
    const concentration = total > 0 ? Math.round((top.value / total) * 100) : 0;
    const segmentLabel = String(block?.xLabel || 'segment').toLowerCase();
    const label = concentration >= 55
      ? 'Concentrated footprint'
      : concentration >= 35
        ? 'Clear lead segment'
        : 'Distributed footprint';

    return {
      label,
      topLabel: this.formatChartLabel(top.label),
      topValue: top.value,
      concentration,
      detail: `${this.formatChartLabel(top.label)} is the largest observed ${segmentLabel} slice with ${top.value.toLocaleString()} detections, ${concentration}% of displayed coverage. Treat this as install footprint context, while the trend chart above shows direction over time.`,
    };
  }
  getHistoricalTrendRead(block: any):
    | { label: string; tone: 'accelerating' | 'growing' | 'stable' | 'cooling'; detail: string; changeLabel: string; change: string; latest: number; peak: number }
    | undefined {
    if (block?.type !== 'historical_trend_chart') {
      return undefined;
    }

    const primarySeries = this.getPrimaryTrendSeries(block);
    const points = (Array.isArray(primarySeries?.points) ? primarySeries.points : [])
      .map(point => ({ label: String(point?.label || ''), value: Number(point?.value) || 0 }))
      .filter(point => point.label);

    if (points.length < 3) {
      return undefined;
    }

    const latest = points[points.length - 1]?.value || 0;
    const peak = Math.max(...points.map(point => point.value));
    const lastWindowSize = Math.min(6, Math.max(2, Math.floor(points.length / 2)));
    const currentWindow = points.slice(-lastWindowSize);
    const previousWindow = points.slice(Math.max(0, points.length - lastWindowSize * 2), points.length - lastWindowSize);
    const currentTotal = currentWindow.reduce((sum, point) => sum + point.value, 0);
    const previousTotal = previousWindow.reduce((sum, point) => sum + point.value, 0);
    const changePercent = previousTotal > 0
      ? Math.round(((currentTotal - previousTotal) / previousTotal) * 100)
      : currentTotal > 0 ? 100 : 0;
    const lastValue = points[points.length - 1].value;
    const firstValue = points[0].value;
    const absoluteChange = lastValue - firstValue;
    const isInstallChart = this.isDetectedInstallTrendBlock(block);

    let tone: 'accelerating' | 'growing' | 'stable' | 'cooling' = 'stable';
    let label = 'Stable';

    if (changePercent >= 30 || absoluteChange > 0 && lastValue >= peak * 0.8) {
      tone = 'accelerating';
      label = 'Accelerating';
    } else if (changePercent >= 8 || absoluteChange > 0) {
      tone = 'growing';
      label = 'Growing';
    } else if (changePercent <= -15 || absoluteChange < 0) {
      tone = 'cooling';
      label = 'Cooling';
    }

    const metricLabel = isInstallChart ? 'verified install detections' : (block?.yLabel || 'trend score').toString().toLowerCase();
    const direction = changePercent > 0 ? 'up' : changePercent < 0 ? 'down' : 'flat';
    const change = `${changePercent > 0 ? '+' : ''}${changePercent}%`;

    return {
      label,
      tone,
      changeLabel: `${lastWindowSize}-period momentum`,
      change,
      latest,
      peak,
      detail: `${this.toTitleCase(metricLabel)} are ${direction} ${change} versus the prior ${previousWindow.length || lastWindowSize} returned periods.`,
    };
  }

  private getPrimaryTrendSeries(block: any): any | undefined {
    const series = this.getChartSeries(block);
    return series.find(item => /detect|install|adoption|usage|momentum/i.test(String(item?.name || ''))) || series[0];
  }

  private isDetectedInstallTrendBlock(block: any): boolean {
    const text = `${block?.title || ''} ${block?.yLabel || ''} ${this.getChartSeries(block).map(item => item?.name || '').join(' ')}`;
    return /install|detection|detected|verified/i.test(text);
  }

  private toTitleCase(value: string): string {
    return String(value ?? '')
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map(word => {
        const parts = word.split(/([\/-])/);
        return parts
          .map(part => /^[\/-]$/.test(part) || !part
            ? part
            : part.charAt(0).toUpperCase() + part.slice(1))
          .join('');
      })
      .join(' ');
  }

  private formatLegendName(value: unknown): string {
    const label = String(value ?? '').replace(/_/g, ' ').trim();
    return label ? this.toTitleCase(label) : 'Series';
  }

  private formatChartLabel(value: unknown): string {
    const label = String(value ?? '').replace(/_/g, ' ').trim();
    return label ? this.toTitleCase(label) : '';
  }

  private getGeneratedLegendOptions(): any {
    return {
      type: 'scroll',
      top: 0,
      itemWidth: 22,
      itemHeight: 12,
      icon: 'roundRect',
      formatter: (name: string) => this.formatLegendName(name),
      textStyle: {
        color: '#263241',
        fontSize: 12,
        fontWeight: 600,
      },
    };
  }

  getGeneratedEchartOptions(block: any): EChartsOption {
    if (block && typeof block === 'object') {
      const cached = this.generatedEchartOptionsCache.get(block);
      if (cached) {
        return cached;
      }
    }

    const options = this.createGeneratedEchartOptions(block);

    if (block && typeof block === 'object') {
      this.generatedEchartOptionsCache.set(block, options);
    }

    return options;
  }

  private createGeneratedEchartOptions(block: any): EChartsOption {
    if (block?.type === 'diagram') {
      return this.getDiagramChartOptions(block);
    }

    if (block?.type === 'radar_chart') {
      return this.getRadarChartOptions(block);
    }

    if (block?.type === 'scatter_chart') {
      return this.getScatterChartOptions(block);
    }

    return this.getSeriesChartOptions(block);
  }

  onGeneratedEchartClick(event: any, block: any): void {
    if (block?.type === 'diagram') {
      const data = event?.data ?? {};
      const isEdge = event?.dataType === 'edge';
      const edgeLabel = isEdge ? data?.relationship || data?.label || `${data?.source ?? ''} -> ${data?.target ?? ''}` : undefined;

      this.generatedUiSelection = {
        blockTitle: block.title,
        title: isEdge ? `${this.getDiagramNodeLabel(block, data?.source)} -> ${this.getDiagramNodeLabel(block, data?.target)}` : event?.name || data?.label || 'Node',
        detail: isEdge ? edgeLabel : this.getDiagramNodeDetail(block, data?.id || data?.nodeId || data?.name),
      };
      return;
    }

    this.generatedUiSelection = {
      blockTitle: block?.title,
      title: event?.name || 'Data point',
      detail: event?.seriesName,
      value: Array.isArray(event?.value) ? event.value.join(', ') : event?.value,
    };
  }

  isGeneratedSelectionVisible(block: any): boolean {
    return !!this.generatedUiSelection && this.generatedUiSelection.blockTitle === block?.title;
  }

  private getSeriesChartOptions(block: any): EChartsOption {
    const labels = this.getChartLabels(block);
    const palette = ['#673ab7', '#1976d2', '#00897b', '#ef8f00', '#c62828', '#5d6d7e'];

    if (block?.type === 'bar_chart') {
      const rankedLabels = [...labels].sort((a, b) => this.getLabelTotal(block, b) - this.getLabelTotal(block, a));
      const barSeries: any[] = this.getChartSeries(block).map(item => {
        const points = Array.isArray(item?.points) ? item.points : [];
        const valueByLabel = new Map(points.map(point => [point?.label, Number(point?.value) || 0]));

        return {
          name: this.formatLegendName(item?.name || 'Series'),
          type: 'bar',
          barMaxWidth: 22,
          emphasis: { focus: 'series' },
          label: {
            show: rankedLabels.length <= 8,
            position: 'right',
            color: '#405064',
            fontSize: 10,
          },
          data: rankedLabels.map(label => valueByLabel.get(label) ?? 0),
        };
      });

      return {
        color: palette,
        animationDuration: 520,
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params: any) => this.formatGeneratedChartTooltip(params, block),
        },
        legend: this.getGeneratedLegendOptions(),
        toolbox: {
          right: 4,
          feature: {
            dataView: { readOnly: true },
            restore: {},
            saveAsImage: {},
          },
        },
        grid: {
          top: 48,
          right: 46,
          bottom: 42,
          left: 132,
          containLabel: true,
        },
        xAxis: {
          type: 'value',
          name: this.formatChartLabel(block?.yLabel || ''),
          nameLocation: 'middle',
          nameGap: 28,
          splitLine: { lineStyle: { color: '#edf1f5' } },
        },
        yAxis: {
          type: 'category',
          inverse: true,
          name: '',
          axisTick: { alignWithLabel: true },
          axisLabel: {
            color: '#425066',
            fontSize: 10,
            formatter: (value: string) => this.formatChartLabel(value),
            width: 150,
            overflow: 'truncate',
          },
          data: rankedLabels,
        },
        series: barSeries,
      };
    }

    const historicalEvents = this.getHistoricalEvents(block);
    const isDetectedInstallTrend = this.isDetectedInstallTrendBlock(block);
    const lineSeries: any[] = this.getChartSeries(block).map((item, index) => {
      const points = Array.isArray(item?.points) ? item.points : [];
      const valueByLabel = new Map(points.map(point => [point?.label, Number(point?.value) || 0]));
      const renderInstallDetectionsAsBars = isDetectedInstallTrend && index === 0;

      return {
        name: this.formatLegendName(item?.name || 'Series'),
        type: renderInstallDetectionsAsBars ? 'bar' : 'line',
        smooth: !renderInstallDetectionsAsBars,
        barMaxWidth: renderInstallDetectionsAsBars ? 24 : undefined,
        symbol: renderInstallDetectionsAsBars ? 'none' : 'circle',
        symbolSize: renderInstallDetectionsAsBars ? 0 : block?.type === 'historical_trend_chart' ? 8 : 6,
        showSymbol: !renderInstallDetectionsAsBars,
        emphasis: { focus: 'series' },
        lineStyle: renderInstallDetectionsAsBars ? undefined : { width: block?.type === 'historical_trend_chart' ? 3 : 2 },
        itemStyle: renderInstallDetectionsAsBars
          ? {
              borderRadius: [4, 4, 0, 0],
            }
          : undefined,
        areaStyle: renderInstallDetectionsAsBars
          ? undefined
          : {
              opacity: index === 0 ? 0.14 : 0.06,
            },
        data: labels.map(label => valueByLabel.get(label) ?? 0),
        connectNulls: true,
        markPoint: block?.type === 'historical_trend_chart' && index === 0
          ? {
              symbol: 'pin',
              symbolSize: 48,
              label: { fontSize: 9 },
              data: historicalEvents
                .map(event => {
                  const labelIndex = labels.indexOf(event.date);
                  return labelIndex >= 0
                    ? {
                        name: event.label,
                        coord: [event.date, valueByLabel.get(event.date) ?? 0],
                        value: event.confidence || '',
                      }
                    : undefined;
                })
                .filter(Boolean),
            }
          : undefined,
        markLine: block?.type === 'historical_trend_chart' && historicalEvents.length
          ? {
              silent: true,
              symbol: 'none',
              lineStyle: { color: '#b8c2d0', type: 'dashed', width: 1 },
              label: {
                formatter: (params: any) => params?.name || '',
                color: '#607086',
                fontSize: 9,
              },
              data: historicalEvents
                .filter(event => labels.includes(event.date))
                .map(event => ({ name: event.label, xAxis: event.date })),
            }
          : undefined,
      };
    });

    return {
      color: palette,
      animationDuration: 520,
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        formatter: (params: any) => this.formatGeneratedChartTooltip(params, block),
      },
      legend: this.getGeneratedLegendOptions(),
      toolbox: {
        right: 4,
        feature: {
          dataZoom: { yAxisIndex: 'none' },
          dataView: { readOnly: true },
          restore: {},
          saveAsImage: {},
        },
      },
      grid: {
        top: 48,
        right: 28,
        bottom: labels.length > 5 ? 58 : 38,
        left: 52,
        containLabel: true,
      },
      dataZoom: labels.length > 6
        ? [
            { type: 'inside', xAxisIndex: 0 },
            { type: 'slider', xAxisIndex: 0, height: 18, bottom: 8 },
          ]
        : undefined,
      xAxis: {
        type: 'category',
        boundaryGap: isDetectedInstallTrend,
        name: this.formatChartLabel(block?.xLabel || ''),
        nameLocation: 'middle',
        nameGap: 32,
        axisLabel: {
          fontSize: 10,
          formatter: (value: string) => this.formatChartLabel(value),
          interval: 0,
          rotate: labels.length > 5 ? 25 : 0,
        },
        data: labels,
      },
      yAxis: {
        type: 'value',
        name: this.formatChartLabel(block?.yLabel || ''),
        nameTextStyle: { fontSize: 10 },
        splitLine: { lineStyle: { color: '#edf1f5' } },
      },
      series: lineSeries,
    };
  }

  private getScatterChartOptions(block: any): EChartsOption {
    const labels = this.getChartLabels(block);
    const series: any[] = this.getChartSeries(block).map(item => {
      const points = Array.isArray(item?.points) ? item.points : [];
      return {
        name: this.formatLegendName(item?.name || 'Series'),
        type: 'scatter',
        symbolSize: (value: any) => 12 + Math.min(Math.max(Number(value?.[1]) || 0, 0) * 4, 20),
        emphasis: {
          focus: 'series',
          label: { show: true, formatter: (params: any) => params?.name || '', position: 'top' },
        },
        data: points.map(point => ({
          name: this.formatChartLabel(point?.label),
          value: [point?.label, Number(point?.value) || 0],
        })),
      };
    });

    return {
      color: ['#673ab7', '#1976d2', '#00897b', '#ef8f00', '#c62828', '#5d6d7e'],
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => `<b>${params?.name || ''}</b><br/>${params?.seriesName}: <b>${params?.value?.[1] ?? ''}</b>`,
      },
      legend: this.getGeneratedLegendOptions(),
      toolbox: {
        right: 4,
        feature: {
          dataZoom: { yAxisIndex: 'none' },
          dataView: { readOnly: true },
          restore: {},
          saveAsImage: {},
        },
      },
      grid: {
        top: 50,
        right: 24,
        bottom: labels.length > 5 ? 58 : 38,
        left: 52,
        containLabel: true,
      },
      dataZoom: labels.length > 6
        ? [
            { type: 'inside', xAxisIndex: 0 },
            { type: 'slider', xAxisIndex: 0, height: 18, bottom: 8 },
          ]
        : undefined,
      xAxis: {
        type: 'category',
        name: this.formatChartLabel(block?.xLabel || ''),
        nameLocation: 'middle',
        nameGap: 34,
        axisLabel: { fontSize: 10, formatter: (value: string) => this.formatChartLabel(value), interval: 0, rotate: labels.length > 5 ? 25 : 0 },
        data: labels,
      },
      yAxis: {
        type: 'value',
        name: this.formatChartLabel(block?.yLabel || ''),
        splitLine: { lineStyle: { color: '#edf1f5' } },
      },
      series,
    };
  }

  private getRadarChartOptions(block: any): EChartsOption {
    const labels = this.getChartLabels(block);
    const maxValue = Math.max(
      5,
      ...this.getChartSeries(block).flatMap(series =>
        (Array.isArray(series?.points) ? series.points : []).map(point => Number(point?.value) || 0)
      )
    );
    const seriesData: any[] = this.getChartSeries(block).map(series => {
      const points = Array.isArray(series?.points) ? series.points : [];
      const valueByLabel = new Map(points.map(point => [point?.label, Number(point?.value) || 0]));
      return {
        name: this.formatLegendName(series?.name || 'Series'),
        value: labels.map(label => Number(valueByLabel.get(label)) || 0),
        areaStyle: { opacity: 0.12 },
      };
    });

    return {
      color: ['#673ab7', '#1976d2', '#00897b', '#ef8f00', '#c62828', '#5d6d7e'],
      tooltip: { trigger: 'item' },
      legend: this.getGeneratedLegendOptions(),
      toolbox: {
        right: 4,
        feature: {
          dataView: { readOnly: true },
          restore: {},
          saveAsImage: {},
        },
      },
      radar: {
        radius: '62%',
        center: ['50%', '56%'],
        splitNumber: 4,
        axisName: {
          color: '#425066',
          fontSize: 10,
        },
        splitLine: { lineStyle: { color: '#dfe6ee' } },
        splitArea: { areaStyle: { color: ['#fbfcfd', '#f3f6fa'] } },
        indicator: labels.map(label => ({
          name: this.formatChartLabel(label),
          max: Math.ceil(maxValue),
        })),
      },
      series: [
        {
          type: 'radar',
          symbol: 'circle',
          symbolSize: 5,
          data: seriesData,
        },
      ],
    };
  }

  private getLabelTotal(block: any, label: string): number {
    return this.getChartSeries(block).reduce((total, series) => {
      const point = (Array.isArray(series?.points) ? series.points : [])
        .find(item => item?.label === label);
      return total + (Number(point?.value) || 0);
    }, 0);
  }

  getChartSignals(block: any): Array<{ label: string; displayLabel: string; total: number; summary: string }> {
    return this.getChartLabels(block).map(label => {
      const parts = this.getChartSeries(block)
        .map(series => {
          const point = (Array.isArray(series?.points) ? series.points : [])
            .find(item => item?.label === label);
          return point ? `${this.formatLegendName(series?.name || 'Series')} ${Number(point.value) || 0}` : '';
        })
        .filter(Boolean);

      return {
        label,
        displayLabel: this.formatChartLabel(label),
        total: this.getLabelTotal(block, label),
        summary: parts.join(' · '),
      };
    });
  }

  selectChartSignal(block: any, signal: any): void {
    this.generatedUiSelection = {
      blockTitle: block?.title,
      title: signal?.displayLabel || this.formatChartLabel(signal?.label) || 'Signal',
      detail: signal?.summary || block?.title,
      value: Number(signal?.total || 0).toFixed(1),
    };
  }

  private getChartLabels(block: any): string[] {
    const labels = new Set<string>();
    this.getChartSeries(block).forEach(series => {
      (Array.isArray(series?.points) ? series.points : []).forEach(point => {
        if (point?.label) {
          labels.add(point.label);
        }
      });
    });
    return Array.from(labels);
  }

  getHistoricalEvents(block: any): any[] {
    const events = block?.events;
    return Array.isArray(events) ? events : [];
  }

  selectHistoricalEvent(block: any, event: any): void {
    this.generatedUiSelection = {
      blockTitle: block?.title,
      title: `${event?.date}: ${event?.label}`,
      detail: [event?.detail, event?.sourceType].filter(Boolean).join(' · '),
      value: event?.confidence,
    };
  }

  private formatGeneratedChartTooltip(params: any, block: any): string {
    const items = Array.isArray(params) ? params : [params];
    const label = items[0]?.axisValueLabel || items[0]?.name || '';
    const displayLabel = this.formatChartLabel(label);
    const event = this.getHistoricalEvents(block).find(item => item?.date === label);
    const values = items
      .map(item => `${item.marker || ''}${item.seriesName}: <b>${item.value}</b>`)
      .join('<br/>');
    const eventHtml = event
      ? `<br/><br/><b>${event.label}</b><br/>${event.detail || ''}${event.confidence ? `<br/>Confidence: ${event.confidence}` : ''}`
      : '';

    return `<b>${label}</b><br/>${values}${eventHtml}`;
  }

  private getDiagramChartOptions(block: any): EChartsOption {
    const nodes = this.getDiagramNodes(block);
    const edges = this.getDiagramEdges(block);
    const groups = Array.from(new Set(nodes.map(node => node?.group).filter(Boolean)));
    const degreeByNode = new Map<string, number>();

    edges.forEach(edge => {
      degreeByNode.set(edge.from, (degreeByNode.get(edge.from) ?? 0) + 1);
      degreeByNode.set(edge.to, (degreeByNode.get(edge.to) ?? 0) + 1);
    });

    const centerNodeId = this.getDiagramCenterNodeId(nodes, edges, degreeByNode);

    return {
      color: ['#673ab7', '#1976d2', '#00897b', '#ef8f00', '#5d6d7e', '#c62828'],
      animationDuration: 650,
      animationEasing: 'cubicOut',
      tooltip: {
        trigger: 'item',
        confine: true,
        appendToBody: false,
        backgroundColor: '#ffffff',
        borderColor: '#d9e0e8',
        borderWidth: 1,
        padding: [8, 10],
        textStyle: {
          color: '#253142',
          fontSize: 12,
          lineHeight: 18,
        },
        extraCssText: 'max-width:260px;white-space:normal;box-shadow:0 8px 22px rgba(15,23,42,0.12);',
        formatter: (params: any) => {
          if (params.dataType === 'edge') {
            return this.formatDiagramEdgeTooltip(block, params.data);
          }

          return this.formatDiagramNodeTooltip(block, params.data);
        },
      },
      legend: groups.length
        ? {
            type: 'scroll',
            top: 2,
            left: 'center',
            formatter: (name: string) => this.formatLegendName(name),
            textStyle: { color: '#263241', fontSize: 11, fontWeight: 600 },
            itemWidth: 14,
            itemHeight: 10,
          }
        : undefined,
      toolbox: {
        right: 4,
        feature: {
          restore: {},
          saveAsImage: {},
        },
      },
      series: [
        {
          type: 'graph',
          layout: 'force',
          top: groups.length ? 36 : 14,
          bottom: 18,
          left: 22,
          right: 22,
          roam: true,
          draggable: true,
          edgeSymbol: ['none', 'arrow'],
          edgeSymbolSize: [0, 8],
          categories: groups.map(name => ({ name: this.formatLegendName(name) })),
          data: nodes.map(node => ({
            id: node.id,
            nodeId: node.id,
            name: node.label,
            group: node.group,
            category: groups.indexOf(node.group),
            degree: degreeByNode.get(node.id) ?? 0,
            symbolSize: node.id === centerNodeId
              ? 56
              : 34 + Math.min((degreeByNode.get(node.id) ?? 0) * 4, 14),
            value: node.group,
            label: {
              show: true,
              position: node.id === centerNodeId ? 'inside' : 'right',
              color: node.id === centerNodeId ? '#ffffff' : '#253142',
              fontWeight: node.id === centerNodeId ? 700 : 500,
              fontSize: node.id === centerNodeId ? 10 : 10,
              width: node.id === centerNodeId ? 48 : 96,
              overflow: 'break',
              lineHeight: 13,
              backgroundColor: node.id === centerNodeId ? 'transparent' : 'rgba(255,255,255,0.88)',
              borderColor: node.id === centerNodeId ? 'transparent' : '#d9e0e8',
              borderWidth: node.id === centerNodeId ? 0 : 1,
              borderRadius: 4,
              padding: node.id === centerNodeId ? 0 : [3, 5],
            },
          })),
          links: edges.map(edge => ({
            source: edge.from,
            target: edge.to,
            relationship: edge.label,
            lineStyle: {
              width: 1.7,
              curveness: 0.1,
              opacity: 0.62,
            },
          })),
          edgeLabel: {
            show: false,
          },
          lineStyle: {
            color: 'source',
            curveness: 0.16,
            opacity: 0.62,
          },
          force: {
            initLayout: 'circular',
            repulsion: 260,
            gravity: 0.12,
            edgeLength: [84, 132],
            friction: 0.62,
          },
          emphasis: {
            focus: 'adjacency',
            scale: true,
            label: { show: true },
            lineStyle: { width: 3, opacity: 1 },
          },
        },
      ],
    };
  }

  private getDiagramCenterNodeId(nodes: any[], edges: any[], degreeByNode: Map<string, number>): string {
    const focusNode = nodes.find(node => String(node?.group || '').toLowerCase() === 'focus');
    if (focusNode?.id) {
      return focusNode.id;
    }

    const nodeConnectedFromEdges = edges
      .flatMap(edge => [edge?.from, edge?.to])
      .filter(Boolean);

    if (nodeConnectedFromEdges.length) {
      return nodeConnectedFromEdges
        .sort((a, b) => (degreeByNode.get(b) ?? 0) - (degreeByNode.get(a) ?? 0))[0];
    }

    return nodes[0]?.id || '';
  }

  private formatDiagramNodeTooltip(block: any, data: any): string {
    const title = this.escapeTooltipText(data?.name || data?.label || 'Relationship node');
    const group = this.escapeTooltipText(data?.group || block?.diagramType || 'Relationship map');
    const degree = Number(data?.degree || 0);

    return [
      `<b>${title}</b>`,
      group,
      `${degree} relationship${degree === 1 ? '' : 's'}`,
    ].join('<br/>');
  }

  private formatDiagramEdgeTooltip(block: any, data: any): string {
    const source = this.getDiagramNodeLabel(block, data?.source || data?.from);
    const target = this.getDiagramNodeLabel(block, data?.target || data?.to);
    const relationship = this.escapeTooltipText(data?.relationship || data?.label || 'Related');
    const title = source && target
      ? `${this.escapeTooltipText(source)} -> ${this.escapeTooltipText(target)}`
      : 'Relationship';

    return `<b>${title}</b><br/>${relationship}`;
  }

  private escapeTooltipText(value: unknown): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .trim();
  }

  getDiagramNodes(block: any): any[] {
    const nodes = block?.nodes;
    return Array.isArray(nodes) ? nodes : [];
  }

  getDiagramEdges(block: any): any[] {
    const edges = block?.edges;
    return Array.isArray(edges) ? edges : [];
  }

  getDiagramNodeLabel(block: any, nodeId: string): string {
    const node = this.getDiagramNodes(block).find(item => item?.id === nodeId);
    return node?.label || nodeId;
  }

  getDiagramNodeDetail(block: any, nodeIdOrLabel: string): string {
    const nodes = this.getDiagramNodes(block);
    const edges = this.getDiagramEdges(block);
    const node = nodes.find(item => item?.id === nodeIdOrLabel || item?.label === nodeIdOrLabel);
    if (!node) {
      return block?.diagramType || 'Relationship map';
    }

    const connectedEdges = edges.filter(edge => edge?.from === node.id || edge?.to === node.id);
    const connectedLabels = connectedEdges
      .map(edge => this.getDiagramNodeLabel(block, edge.from === node.id ? edge.to : edge.from))
      .slice(0, 4)
      .join(', ');

    return [node.group, connectedEdges.length ? `${connectedEdges.length} relationship(s)` : '', connectedLabels]
      .filter(Boolean)
      .join(' · ');
  }

  getDiagramGroups(block: any): Array<{ name: string; count: number }> {
    const counts = new Map<string, number>();
    this.getDiagramNodes(block).forEach(node => {
      const group = node?.group || 'Other';
      counts.set(group, (counts.get(group) ?? 0) + 1);
    });

    return Array.from(counts.entries()).map(([name, count]) => ({ name, count }));
  }

  selectDiagramGroup(block: any, group: string): void {
    const nodes = this.getDiagramNodes(block)
      .filter(node => (node?.group || 'Other') === group)
      .map(node => node?.label)
      .filter(Boolean);

    this.generatedUiSelection = {
      blockTitle: block?.title,
      title: group,
      detail: nodes.join(', '),
      value: `${nodes.length} node(s)`,
    };
  }

  getDiagramConnections(block: any): Array<{ from: string; to: string; label: string; summary: string }> {
    return this.getDiagramEdges(block).map(edge => {
      const from = this.getDiagramNodeLabel(block, edge?.from);
      const to = this.getDiagramNodeLabel(block, edge?.to);
      const label = edge?.label || 'related';
      return {
        from,
        to,
        label,
        summary: `${from} -> ${to}: ${label}`,
      };
    });
  }

  selectDiagramConnection(block: any, connection: any): void {
    this.generatedUiSelection = {
      blockTitle: block?.title,
      title: `${connection.from} -> ${connection.to}`,
      detail: connection.label,
    };
  }

  async exportData(
    mode: 'product' | 'company',
    buyercaddyData,
    geminiData
  ): Promise<void> {
    console.log('buyercaddyData', buyercaddyData);
    console.log('geminiData', geminiData);

    this.mergeTwoAndDownloadCsv(buyercaddyData, geminiData);
   
  }


  canExportRachelAnswerPdf(message: ChatMessage): boolean {
    return !!message
      && message.sender === 'bot'
      && !message.isTyping
      && message.trendAgentResponse === true;
  }

  isRachelPdfExporting(message?: ChatMessage): boolean {
    return message ? this.rachelPdfExportingMessage === message : !!this.rachelPdfExportingMessage;
  }

  async exportRachelAnswerPdf(message: ChatMessage, contentElement: HTMLElement): Promise<void> {
    if (!contentElement || message?.isTyping || this.isRachelPdfExporting()) {
      return;
    }

    this.rachelPdfExportingMessage = message;
    this.scheduleGeneratedChartResize();
    await new Promise(resolve => setTimeout(resolve, 160));

    const exportId = `rachel-pdf-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    contentElement.setAttribute('data-rachel-pdf-export-id', exportId);

    try {
      const html2canvasModule = await import('html2canvas');
      const html2canvas = (html2canvasModule as any).default || html2canvasModule;
      const { jsPDF } = await import('jspdf');
      const renderWidth = Math.max(contentElement.scrollWidth, contentElement.offsetWidth, 1060);
      const renderHeight = Math.max(contentElement.scrollHeight, contentElement.offsetHeight, window.innerHeight);
      let pdfAvoidRanges: Array<{ top: number; bottom: number }> = [];
      let pdfLayoutHeight = renderHeight;
      const canvas = await html2canvas(contentElement, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        windowWidth: renderWidth,
        windowHeight: renderHeight,
        scrollX: 0,
        scrollY: -window.scrollY,
        ignoreElements: (element: Element) => element.classList?.contains('ignore-pdf'),
        onclone: (clonedDocument: Document) => {
          const clonedContent = clonedDocument.querySelector(`[data-rachel-pdf-export-id="${exportId}"]`) as HTMLElement | null;
          if (!clonedContent) {
            return;
          }

          this.copyCanvasBitmapsForPdfClone(contentElement, clonedContent);
          this.prepareRachelPdfClone(clonedContent);
          clonedContent.style.background = '#ffffff';
          clonedContent.style.boxSizing = 'border-box';
          clonedContent.style.color = '#243244';
          clonedContent.style.fontFamily = 'Arial, Helvetica, sans-serif';
          clonedContent.style.fontSize = '12px';
          clonedContent.style.lineHeight = '1.45';
          clonedContent.style.width = `${renderWidth}px`;

          const pdfLayout = this.getRachelPdfAvoidLayout(clonedContent);
          pdfAvoidRanges = pdfLayout.ranges;
          pdfLayoutHeight = pdfLayout.height;
        },
      });

      if (!canvas?.width || !canvas?.height) {
        throw new Error('BuyerCaddy PDF render produced an empty canvas.');
      }

      const avoidRanges = this.scaleRachelPdfAvoidRanges(pdfAvoidRanges, canvas.height, pdfLayoutHeight);
      this.saveRachelPdfCanvas(canvas, `${this.createRachelPdfFilename(message)}.pdf`, jsPDF, avoidRanges);
    } catch (error) {
      console.error('Failed to export BuyerCaddy answer PDF:', error);
      this.toastr.error('Could not export this BuyerCaddy trend answer as PDF. Please try again.', 'PDF export failed');
    } finally {
      contentElement.removeAttribute('data-rachel-pdf-export-id');
      this.rachelPdfExportingMessage = undefined;
    }
  }

  private saveRachelPdfCanvas(canvas: HTMLCanvasElement, filename: string, JsPdfCtor: any, avoidRanges: Array<{ top: number; bottom: number }> = []): void {
    const pdf = new JsPdfCtor({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 8;
    const printableWidth = pageWidth - margin * 2;
    const printableHeight = pageHeight - margin * 2;
    const pixelsPerPage = Math.floor((printableHeight * canvas.width) / printableWidth);
    const pageCanvas = document.createElement('canvas');
    const pageContext = pageCanvas.getContext('2d');

    if (!pageContext || pixelsPerPage <= 0) {
      throw new Error('BuyerCaddy PDF page canvas could not be initialized.');
    }

    pageCanvas.width = canvas.width;

    let sourceY = 0;
    let pageIndex = 0;
    while (sourceY < canvas.height) {
      const maxSliceHeight = Math.min(pixelsPerPage, canvas.height - sourceY);
      const sliceHeight = this.getRachelPdfSliceHeight(sourceY, maxSliceHeight, pixelsPerPage, avoidRanges);
      pageCanvas.height = sliceHeight;
      pageContext.clearRect(0, 0, pageCanvas.width, pageCanvas.height);
      pageContext.fillStyle = '#ffffff';
      pageContext.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      pageContext.drawImage(canvas, 0, sourceY, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);

      if (pageIndex > 0) {
        pdf.addPage();
      }

      const imageHeight = (sliceHeight * printableWidth) / canvas.width;
      pdf.addImage(pageCanvas.toDataURL('image/jpeg', 0.98), 'JPEG', margin, margin, printableWidth, imageHeight);
      sourceY += sliceHeight;
      pageIndex += 1;
    }

    pdf.save(filename);
  }

  private getRachelPdfAvoidLayout(contentElement: HTMLElement): { height: number; ranges: Array<{ top: number; bottom: number }> } {
    const contentRect = contentElement.getBoundingClientRect();
    const height = Math.max(contentElement.scrollHeight, contentElement.offsetHeight, contentRect.height, 1);
    const avoidSelector = [
      '.generated-echart-block',
      '.generated-echart',
      '.generated-trend-read',
      '.generated-footprint-read',
      '.generated-table-wrap',
      '.generated-table-wrap table',
      '.generated-table-wrap thead',
      '.generated-table-wrap tr',
      '.generated-timeline',
      '.generated-ui-block',
    ].join(', ');

    const ranges = Array.from(contentElement.querySelectorAll(avoidSelector))
      .map(element => {
        const rect = (element as HTMLElement).getBoundingClientRect();
        const top = Math.max(0, Math.floor(rect.top - contentRect.top - 10));
        const bottom = Math.min(height, Math.ceil(rect.bottom - contentRect.top + 10));
        return { top, bottom };
      })
      .filter(range => range.bottom > range.top)
      .sort((a, b) => a.top - b.top);

    return { height, ranges };
  }

  private scaleRachelPdfAvoidRanges(
    ranges: Array<{ top: number; bottom: number }>,
    canvasHeight: number,
    layoutHeight: number,
  ): Array<{ top: number; bottom: number }> {
    const scaleY = canvasHeight / Math.max(layoutHeight, 1);
    return ranges.map(range => ({
      top: Math.max(0, Math.floor(range.top * scaleY)),
      bottom: Math.min(canvasHeight, Math.ceil(range.bottom * scaleY)),
    }));
  }

  private getRachelPdfSliceHeight(
    sourceY: number,
    maxSliceHeight: number,
    pageHeight: number,
    avoidRanges: Array<{ top: number; bottom: number }>,
  ): number {
    const sliceEnd = sourceY + maxSliceHeight;
    const guard = 24;
    const crossingRange = avoidRanges.find(range => {
      const rangeHeight = range.bottom - range.top;
      return rangeHeight < pageHeight - guard * 2
        && range.top > sourceY + guard
        && range.top < sliceEnd - guard
        && range.bottom > sliceEnd;
    });

    if (!crossingRange) {
      return maxSliceHeight;
    }

    return Math.max(guard, Math.floor(crossingRange.top - sourceY - guard));
  }

  private copyCanvasBitmapsForPdfClone(sourceContent: HTMLElement, clonedContent: HTMLElement): void {
    const sourceCanvases = Array.from(sourceContent.querySelectorAll('canvas'));
    const clonedCanvases = Array.from(clonedContent.querySelectorAll('canvas'));

    sourceCanvases.forEach((sourceCanvas, index) => {
      const clonedCanvas = clonedCanvases[index];
      if (!clonedCanvas || !sourceCanvas.width || !sourceCanvas.height) {
        return;
      }

      clonedCanvas.width = sourceCanvas.width;
      clonedCanvas.height = sourceCanvas.height;
      clonedCanvas.style.width = sourceCanvas.style.width || `${sourceCanvas.offsetWidth || sourceCanvas.width}px`;
      clonedCanvas.style.height = sourceCanvas.style.height || `${sourceCanvas.offsetHeight || sourceCanvas.height}px`;

      const context = clonedCanvas.getContext('2d');
      context?.drawImage(sourceCanvas, 0, 0);
    });
  }

  private prepareRachelPdfClone(clonedContent: HTMLElement): void {
    clonedContent
      .querySelectorAll('.ignore-pdf, .echarts-tooltip, .generated-selection, .generated-chart-signals, .generated-diagram-inspector')
      .forEach(element => element.remove());
    clonedContent.querySelectorAll('.generated-event-list').forEach(element => {
      const el = element as HTMLElement;
      el.style.marginTop = '6px';
      el.style.maxHeight = 'none';
      el.style.overflow = 'visible';
    });
    clonedContent.style.minWidth = '0';
    clonedContent.style.width = '100%';

    clonedContent.querySelectorAll('.generated-ui-shell').forEach(element => {
      const el = element as HTMLElement;
      el.style.display = 'block';
      el.style.margin = '12px 0 4px';
      el.style.width = '100%';
    });

    clonedContent.querySelectorAll('.generated-ui-block').forEach(element => {
      const el = element as HTMLElement;
      el.style.background = '#ffffff';
      el.style.border = '0';
      el.style.borderLeft = '0';
      el.style.borderRadius = '0';
      el.style.boxShadow = 'none';
      el.style.breakInside = 'avoid';
      el.style.marginBottom = '12px';
      el.style.padding = '0 0 10px';
      el.style.pageBreakInside = 'avoid';
      el.style.width = '100%';
    });

    clonedContent.querySelectorAll('.generated-trend-read, .generated-footprint-read, .generated-echart').forEach(element => {
      const el = element as HTMLElement;
      el.style.breakInside = 'avoid';
      el.style.pageBreakInside = 'avoid';
    });

    clonedContent.querySelectorAll('.generated-echart').forEach(element => {
      const el = element as HTMLElement;
      const isHistorical = el.classList.contains('generated-echart--historical');
      const isRanking = el.classList.contains('generated-echart--ranking');
      const height = isHistorical ? '360px' : isRanking ? '340px' : '320px';
      el.style.height = height;
      el.style.minHeight = height;
      el.style.overflow = 'visible';
      el.style.width = '100%';

      el.querySelectorAll('svg').forEach(svgElement => {
        const svg = svgElement as SVGElement;
        svg.setAttribute('width', '100%');
        svg.setAttribute('height', height.replace('px', ''));
        svg.style.height = height;
        svg.style.overflow = 'visible';
        svg.style.width = '100%';
      });
    });
  }

  private getPdfTitle(message: ChatMessage): string {
    const textTitle = this.extractFirstMarkdownHeading(message?.text || '');
    if (textTitle) {
      return textTitle;
    }

    const firstBlockTitle = message?.generatedUi?.blocks?.find(block => !!block?.title)?.title;
    return firstBlockTitle || 'BuyerCaddy answer';
  }

  private extractFirstMarkdownHeading(value: string): string {
    const plain = value.replace(/<[^>]+>/g, '').trim();
    const heading = plain.split(/\r?\n/).map(line => line.trim()).find(line => /^#{1,3}\s+/.test(line));
    return heading ? heading.replace(/^#{1,3}\s+/, '').trim() : '';
  }

  private createRachelPdfFilename(message: ChatMessage): string {
    const title = this.getPdfTitle(message)
      .toLowerCase()
      .replace(/[^a-z0-9а-яё]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 72) || 'buyercaddy-answer';
    const now = new Date();
    const pad = (n: number, width = 2) => String(n).padStart(width, '0');
    return `${title}-${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
  getDisplayColumns(): string[] {
    const isAggressive = this.aiChatService.aggressiveCustomersMode;
    const baseColumns = [
      'Company',
      'CompanyDomain',
      'Product',
      'Vendor',
      'VendorDomain',
      'Country',
      'HitCount',
      'DateFirstVerified',
      'DateLastVerified',
    ];
    if (isAggressive) {
      baseColumns.push('Source');
    }
    return baseColumns;
  }

  getNormalizedData(firstArray: any[], secondArray: any[], deduplicate: boolean = false): any[] {
    // Преобразование даты из разных форматов
    const getDate = (row: any, upperKey: string, lowerKey: string): string => {
      const v = row?.[upperKey]?.value ?? row?.[lowerKey] ?? row?.[upperKey] ?? '';
      if (!v) return '';
      try {
        const d = new Date(v);
        return isNaN(d.getTime()) ? v : d.toLocaleDateString();
      } catch {
        return v;
      }
    };

    // Нормализация объекта под целевые колонки
    const getSource = (row: any, fallbackSource: string = '') =>
      row?.Source?.value ?? row?.Source ?? fallbackSource ?? '';

    const normalize = (row: any, fallbackSource: string = '', forceFallback: boolean = false) => ({
      Company: row?.Company ?? row?.company ?? '',
      CompanyDomain: row?.CompanyDomain ?? row?.company_domain ?? row?.companydomain ?? '',
      Product: row?.Product ?? row?.product ?? row?.product_name ?? '',
      Vendor: row?.Vendor ?? row?.vendor ?? row?.product_vendor ?? '',
      VendorDomain: row?.VendorDomain ?? row?.vendordomain ?? row?.product_vendor_domain_name ?? '',
      Country: row?.Country ?? row?.country ?? '',
      HitCount: typeof row?.HitCount === 'number' ? row.HitCount : (row?.HitCount ? Number(row.HitCount) : (row?.hit_count ?? '')),
      DateFirstVerified: getDate(row, 'DateFirstVerified', 'dateFirstVerified'),
      DateLastVerified: getDate(row, 'DateLastVerified', 'dateLastVerified'),
      Source: forceFallback ? fallbackSource : getSource(row, fallbackSource),
    });

    const firstRows = (firstArray ?? []).map(row => normalize(row, 'BuyerCaddy', true));
    const secondRows = (secondArray ?? []).map(row => normalize(row));
    let rows = [...firstRows, ...secondRows];

    if (deduplicate) {
      const seenDomainProductKeys = new Set<string>();
      const seenNameProductKeys = new Set<string>();
      const seenNameProductKeysWithoutDomain = new Set<string>();

      rows = rows.filter(r => {
        const product = this.normalizeDeduplicationText(r.Product);
        const companyDomain = this.normalizeDomainForDeduplication(r.CompanyDomain);
        const companyName = this.normalizeDeduplicationText(r.Company);

        // Incomplete rows cannot be matched safely and must remain visible.
        if (!product || (!companyDomain && !companyName)) {
          return true;
        }

        const domainProductKey = companyDomain ? `${companyDomain}||${product}` : '';
        const nameProductKey = companyName ? `${companyName}||${product}` : '';
        const isDuplicate = companyDomain
          ? seenDomainProductKeys.has(domainProductKey) ||
            (!!nameProductKey && seenNameProductKeysWithoutDomain.has(nameProductKey))
          : !!nameProductKey && seenNameProductKeys.has(nameProductKey);

        if (isDuplicate) {
          return false;
        }

        if (domainProductKey) {
          seenDomainProductKeys.add(domainProductKey);
        }
        if (nameProductKey) {
          seenNameProductKeys.add(nameProductKey);
          if (!companyDomain) {
            seenNameProductKeysWithoutDomain.add(nameProductKey);
          }
        }

        return true;
      });
    }
    return rows;
  }

  private normalizeDeduplicationText(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    const scalarValue =
      typeof value === 'object' && 'value' in (value as Record<string, unknown>)
        ? (value as Record<string, unknown>)['value']
        : value;

    return String(scalarValue ?? '')
      .normalize('NFKC')
      .trim()
      .toLowerCase()
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      .replace(/[™®©]/g, '')
      .replace(/\s+/g, ' ');
  }

  private normalizeDomainForDeduplication(value: unknown): string {
    return this.normalizeDeduplicationText(value)
      .replace(/^(?:https?:)?\/\//, '')
      .split(/[/?#]/, 1)[0]
      .replace(/^www\./, '')
      .replace(/:\d+$/, '')
      .replace(/\.$/, '');
  }

  mergeTwoAndDownloadCsv(
    firstArray: any[],
    secondArray: any[],
    deduplicate: boolean = false
  ): void {
    const TARGET_COLUMNS = this.getDisplayColumns();
    const rows = this.getNormalizedData(firstArray, secondArray, deduplicate);

    // Экранирование CSV
    const csvEscape = (value: any): string => {
      const s = value === null || value === undefined ? '' : String(value);
      return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    // Формируем CSV
    const header = TARGET_COLUMNS.join(',');
    const body = rows
      .map(r => TARGET_COLUMNS.map(c => csvEscape((r as any)[c])).join(','))
      .join('\r\n');
    const csv = `${header}\r\n${body}`;

    // Формируем имя файла с миллисекундами
    const now = new Date();
    const pad = (n: number, width = 2) => String(n).padStart(width, '0');
    const filename = `export_${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}-${pad(now.getMilliseconds(), 3)}.csv`;

    // Скачивание файла
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }


 
}
